import { Content, TDocumentDefinitions } from "pdfmake/interfaces";

import ComponyLogoForm, { CompanyType, FontType } from "../../common/CompanyLogoForm";

import CONFIG from "config/config";
import { copyMap, receiptMap, receiptNoMap, ReceiptType } from "../../BIL/FormSapiensTaxInvoice";

type HeaderSapiensTexInvoiceFormProps = Partial<{
  form?: string;
  font?: FontType;
  header: Content | Content[];
  images: TDocumentDefinitions["images"];
  pageMargins: TDocumentDefinitions["pageMargins"];
  styles: TDocumentDefinitions["styles"];
  logoHight?: number;
  hospitalAddress?: any;
  titleMargins?: number[];
  logoMargins?: number[];
  receiptType?: string;
  isWatermark?: boolean;
  created?: string;
  wallet_deposit_code?: string;
  cancelled_code?: string;
  code?: string;
}>;

type ReturnType = {
  font?: string;
  fontSizes: Record<number, number>;
  lineHeights: Record<number, number>;
} & Pick<TDocumentDefinitions, "header" | "images" | "pageMargins" | "styles">;

const FORM_NAME = `HeaderSapiensTexInvoiceForm`;

const HeaderSapiensTexInvoiceForm = async (
  props: HeaderSapiensTexInvoiceFormProps = {},
): Promise<ReturnType> => {
  const formName = props.form || FORM_NAME;
  const defaultFont = props.font || "KanitLM";
  const companyLogo = await ComponyLogoForm({
    font: defaultFont,
    height: props.logoHight || 35,
    type: 2,
    form: formName,
    logoImage: props.images?.oaLogo,
  });
  const { font, fontSizes, images, lineHeights } = companyLogo;

  const hasImage = !!images && Object.keys(images).length > 0;

  const receipt = receiptMap[props.receiptType as ReceiptType];
  const receiptNo = receiptNoMap[props.receiptType as ReceiptType];
  const copy = props.isWatermark ? copyMap.watermark : copyMap.original;

  return {
    font,
    fontSizes,
    images: {
      ...images,
      ...props.images,
    },
    lineHeights,
    // * Logo height 100
    pageMargins: props.pageMargins || [48, 100, 48, 40],
    styles: {
      hospitalInfo: {
        fontSize: 7,
      },
      hospitalName: {
        bold: true,
        fontSize: 7,
      },
      header: {
        bold: true,
        fontSize: 20,
        margin: [0, 0, 0, 2],
      },
      ...props.styles,
    },
    header: () => ({
      margin: [27, 0, 27, 0],
      stack: [
        {
          columnGap: 10,
          columns: [
            {
              stack: [
                {
                  ...companyLogo,
                  margin:
                    CONFIG.COMPANY === "MSK" && props.logoMargins
                      ? props.logoMargins
                      : CONFIG.COMPANY === "MSK"
                        ? [0, 9, 0, 0]
                        : CONFIG.COMPANY === "RAMASRI"
                          ? [0, 0, 0, 0]
                          : !hasImage && CONFIG.COMPANY === "MEHEALTH"
                            ? [0, 48, 0, 0]
                            : [0, 24, 0, 0],
                  width: "*",
                },
                {
                  ...props.hospitalAddress,
                },
              ],
            },
            {
              stack: [
                {
                  text: [
                    {
                      text: receipt?.th,
                      font: "KanitLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                    {
                      text: " / ",
                      font: "PoppinsLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                    {
                      text: "ใบกำกับภาษี",
                      font: "KanitLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                  ],
                },
                {
                  text: [
                    {
                      text: receipt?.en,
                      font: "PoppinsLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                    {
                      text: " / Tax Invoice",
                      font: "PoppinsLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                  ],
                },
                {
                  text: [
                    {
                      text: copy?.th,
                      font: "KanitLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                    {
                      text: copy?.en,
                      font: "PoppinsLM",
                      style: {
                        bold: true,
                        fontSize: 9,
                      },
                    },
                  ],
                },
                {
                  text: `\n`,
                },
                {
                  columns: [
                    {
                      text: [
                        {
                          text: "เลขที่",
                          font: "KanitLM",
                          style: {
                            bold: true,
                            fontSize: 9,
                          },
                        },
                        {
                          text: " / No. : ",
                          font: "PoppinsLM",
                          style: {
                            bold: true,
                            fontSize: 9,
                          },
                        },
                      ],
                    },
                    {
                      width: 75,
                      text: receiptNo?.code?.(props) || "",
                      font: "PoppinsLM",
                      style: {
                        fontSize: 9,
                      },
                    },
                  ],
                },
                {
                  columns: [
                    {
                      text: [
                        {
                          text: "วันที่",
                          font: "KanitLM",
                          style: {
                            bold: true,
                            fontSize: 9,
                          },
                        },
                        {
                          text: " / Date. : ",
                          font: "PoppinsLM",
                          style: {
                            bold: true,
                            fontSize: 9,
                          },
                        },
                      ],
                    },
                    {
                      width: 75,
                      text: props?.created || "",
                      font: "PoppinsLM",
                      style: {
                        fontSize: 9,
                      },
                    },
                  ],
                },
              ],
              alignment: "right",
              lineHeight: 1.05,
            },
          ],
          lineHeight: 1.175,
          margin: props.titleMargins ? props.titleMargins : [0, 16, 0, 27.5],
        },
        ...(Array.isArray(props.header) ? props.header : [props.header || { text: "" }]),
      ],
    }),
  };
};

export default HeaderSapiensTexInvoiceForm;
